import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { DocumentTypeEnum } from '@models/enums/document-type-enum';
import { DocumentApiErrorRow } from './user-form-api-error.types';

export function clearDocumentApiErrors(documents: FormArray): void {
  for (const documentFg of documents.controls) {
    for (const fieldName of ['docTxDocumento', 'docTxOrgaoExpeditor'] as const) {
      const ctrl = documentFg.get(fieldName);
      if (!ctrl?.errors?.['apiError']) {
        continue;
      }
      const { apiError: _apiError, ...remainingErrors } = ctrl.errors;
      ctrl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }
}

function isCpfDocumentValueComplete(ctrl: AbstractControl): boolean {
  const digits = (ctrl.value?.toString() ?? '').replace(/\D/g, '');
  return digits.length === 11;
}

function getDocumentValueLength(ctrl: AbstractControl): number {
  return (ctrl.value?.toString() ?? '').replace(/\D/g, '').length;
}

function pickBestDocumentRow(
  rows: DocumentApiErrorRow[],
  errorMessage?: string,
): DocumentApiErrorRow | null {
  const isGenericLengthMessage = errorMessage
    ?.toLowerCase()
    .includes('caracteres');

  if (isGenericLengthMessage) {
    const lengthValidationRows = rows.filter(
      ({ ctrl }) => ctrl.errors?.['minlength'] || ctrl.errors?.['maxlength'],
    );
    if (lengthValidationRows.length === 1) {
      return lengthValidationRows[0];
    }
    if (lengthValidationRows.length > 1) {
      const nonCpf = lengthValidationRows.filter(
        ({ tipo }) => tipo !== DocumentTypeEnum.CPF,
      );
      if (nonCpf.length > 0) {
        return nonCpf[nonCpf.length - 1];
      }
    }

    const shortValueRows = rows.filter(({ ctrl, tipo }) => {
      const length = getDocumentValueLength(ctrl);
      return (
        length > 0 &&
        length < 5 &&
        !(tipo === DocumentTypeEnum.CPF && length === 11)
      );
    });
    if (shortValueRows.length > 0) {
      return shortValueRows[shortValueRows.length - 1];
    }
  }

  const nonRequiredRows = rows.filter(({ ctrl }) => {
    const errors = ctrl.errors ?? {};
    return Object.keys(errors).some((key) => key !== 'required');
  });
  if (nonRequiredRows.length > 0) {
    const nonCpf = nonRequiredRows.filter(
      ({ tipo }) => tipo !== DocumentTypeEnum.CPF,
    );
    const pool = nonCpf.length > 0 ? nonCpf : nonRequiredRows;
    return pool[pool.length - 1];
  }

  const touchedRows = rows.filter(({ ctrl }) => ctrl.dirty || ctrl.touched);
  if (touchedRows.length > 0) {
    const nonCpf = touchedRows.filter(
      ({ tipo }) => tipo !== DocumentTypeEnum.CPF,
    );
    const pool = nonCpf.length > 0 ? nonCpf : touchedRows;
    return pool[pool.length - 1];
  }

  const nonCpfRows = rows.filter(({ tipo }) => tipo !== DocumentTypeEnum.CPF);
  if (nonCpfRows.length > 0) {
    return nonCpfRows[nonCpfRows.length - 1];
  }

  return rows[rows.length - 1];
}

function guessDocumentControlFromApiMessage(
  errorMessage: string | undefined,
  documentRows: DocumentApiErrorRow[],
): AbstractControl | null {
  const isGenericLengthMessage = errorMessage
    ?.toLowerCase()
    .includes('caracteres');

  const rowsWithValue = documentRows.filter(
    ({ ctrl }) => getDocumentValueLength(ctrl) > 0,
  );

  if (isGenericLengthMessage) {
    const suspiciousRows = rowsWithValue.filter(({ ctrl, tipo }) => {
      const length = getDocumentValueLength(ctrl);
      if (tipo === DocumentTypeEnum.CPF && isCpfDocumentValueComplete(ctrl)) {
        return false;
      }
      return length < 5 || length > 100;
    });
    if (suspiciousRows.length === 1) {
      return suspiciousRows[0].ctrl;
    }
    if (suspiciousRows.length > 0) {
      const nonCpf = suspiciousRows.filter(
        ({ tipo }) => tipo !== DocumentTypeEnum.CPF,
      );
      const pool = nonCpf.length > 0 ? nonCpf : suspiciousRows;
      return pool[pool.length - 1].ctrl;
    }

    const nonCpfWithValue = rowsWithValue.filter(
      ({ tipo, ctrl }) =>
        tipo !== DocumentTypeEnum.CPF && !isCpfDocumentValueComplete(ctrl),
    );
    if (nonCpfWithValue.length > 0) {
      return nonCpfWithValue[nonCpfWithValue.length - 1].ctrl;
    }
  }

  const touchedNonCpf = rowsWithValue.filter(
    ({ ctrl, tipo }) =>
      (ctrl.dirty || ctrl.touched) && tipo !== DocumentTypeEnum.CPF,
  );
  if (touchedNonCpf.length > 0) {
    return touchedNonCpf[touchedNonCpf.length - 1].ctrl;
  }

  const nonCpfWithValue = rowsWithValue.filter(
    ({ tipo }) => tipo !== DocumentTypeEnum.CPF,
  );
  if (nonCpfWithValue.length > 0) {
    return nonCpfWithValue[nonCpfWithValue.length - 1].ctrl;
  }

  return null;
}

/** Quando a API envia label genérico `docTxDocumento`, escolhe a linha correta do FormArray. */
export function findDocumentControlForDocTxDocumentoError(
  documents: FormArray,
  errorMessage?: string,
): AbstractControl | null {
  const documentRows: DocumentApiErrorRow[] = documents.controls.map(
    (control) => {
      const fg = control as FormGroup;
      return {
        fg,
        ctrl: fg.get('docTxDocumento')!,
        tipo: fg.get('docTxTipo')?.value ?? null,
      };
    },
  );

  const isGenericLengthMessage = errorMessage
    ?.toLowerCase()
    .includes('caracteres');

  const cpfRow = documentRows.find(({ tipo }) => tipo === DocumentTypeEnum.CPF);
  const cpfIsComplete =
    cpfRow != null && isCpfDocumentValueComplete(cpfRow.ctrl);

  let candidateRows = documentRows.filter(({ ctrl, tipo }) => {
    if (!ctrl.invalid) {
      return false;
    }
    if (tipo === DocumentTypeEnum.CPF && isCpfDocumentValueComplete(ctrl)) {
      const errorKeys = Object.keys(ctrl.errors ?? {});
      const onlyRequired =
        errorKeys.length === 1 && errorKeys[0] === 'required';
      if (onlyRequired || errorKeys.length === 0) {
        return false;
      }
    }
    return true;
  });

  if (cpfIsComplete) {
    const withoutValidCpf = candidateRows.filter(
      ({ tipo }) => tipo !== DocumentTypeEnum.CPF,
    );
    if (withoutValidCpf.length > 0) {
      candidateRows = withoutValidCpf;
    }
  }

  if (isGenericLengthMessage) {
    const withoutCompleteCpf = candidateRows.filter(
      ({ ctrl, tipo }) =>
        !(tipo === DocumentTypeEnum.CPF && isCpfDocumentValueComplete(ctrl)),
    );
    if (withoutCompleteCpf.length > 0) {
      candidateRows = withoutCompleteCpf;
    }
  }

  if (candidateRows.length === 1) {
    return candidateRows[0].ctrl;
  }

  if (candidateRows.length > 1) {
    const picked = pickBestDocumentRow(candidateRows, errorMessage);
    if (picked) {
      return picked.ctrl;
    }
  }

  return guessDocumentControlFromApiMessage(errorMessage, documentRows);
}

export function findDocumentControlByApiLabel(
  documents: FormArray,
  apiLabel: string,
  errorMessage?: string,
): AbstractControl | null {
  if (apiLabel === 'docTxDocumento') {
    return findDocumentControlForDocTxDocumentoError(documents, errorMessage);
  }

  const documentTypes = Object.values(DocumentTypeEnum) as string[];
  if (documentTypes.includes(apiLabel)) {
    const fg = documents.controls.find(
      (d) => d.get('docTxTipo')?.value === apiLabel,
    );
    return fg?.get('docTxDocumento') ?? null;
  }

  if (apiLabel === 'docTxOrgaoExpeditor') {
    const fg = documents.controls.find(
      (d) => d.get('docTxTipo')?.value === DocumentTypeEnum.RG,
    );
    return fg?.get('docTxOrgaoExpeditor') ?? null;
  }

  return null;
}
